<template>
  <card-container
    id="bulk-transcribe-call-logs"
    hide-reset
    :disabled="!parsedIds.length || isProcessing"
    @submit="startBatch"
  >
    <template #title>
      Bulk Transcribe Call Logs
    </template>

    <template #card-notes>
      <p class="page-description">
        Paste a list of call-log IDs (separated by commas, spaces, or newlines). Each ID is sent to
        the transcription service sequentially. Transcripts are stored in the database for audit
        queries — nothing is displayed here.
      </p>
    </template>

    <template #content>
      <div class="row">
        <label
          class="col-sm-2 control-label text-left"
          for="bulk-transcribe-call-logs-input"
        >
          Call Log IDs:
        </label>
        <div class="col-sm-10">
          <el-input
            id="bulk-transcribe-call-logs-input"
            v-model="rawInput"
            type="textarea"
            :rows="6"
            placeholder="51714450, 51715485, 51715074, ..."
            :disabled="isProcessing"
          />
          <p class="parse-summary">
            <span v-if="parsedIds.length">
              {{ parsedIds.length }} ID(s) parsed{{ skippedTokens.length ? ` · ${skippedTokens.length} skipped (${skippedTokens.join(', ')})` : '' }}.
            </span>
            <span
              v-else
              class="text-muted"
            >
              Enter at least one numeric ID to enable submission.
            </span>
          </p>
        </div>
      </div>

      <div
        v-if="results.length || isProcessing"
        class="results-section"
      >
        <hr>
        <p class="progress-line">
          {{ processedCount }} of {{ parsedIds.length }} processed · {{ doneCount }} done · {{ queuedCount }} queued · {{ errorCount }} failed
        </p>
        <el-table
          :data="results"
          stripe
          size="small"
        >
          <el-table-column
            prop="id"
            label="Call Log ID"
            width="160"
          />
          <el-table-column
            label="Status"
            width="140"
          >
            <template #default="{ row }">
              <el-tag
                :type="statusTagType(row.status)"
                size="small"
              >
                {{ statusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="message"
            label="Detail"
          />
        </el-table>
      </div>
    </template>

    <template #submit-button>
      Transcribe{{ parsedIds.length ? ` ${parsedIds.length}` : '' }}
    </template>

    <template #additional-buttons>
      <p-button
        v-if="isProcessing"
        type="yellow"
        round
        @click="cancelRequested = true"
      >
        Stop
      </p-button>
      <p-button
        v-else-if="results.length"
        type="yellow"
        round
        @click="reset"
      >
        Clear results
      </p-button>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import util from '@/Utils/util';
import { callLogService } from '@/services/callLogs';

type RowStatus = 'done' | 'queued' | 'error' | 'skipped';

interface ResultRow {
  id: number;
  status: RowStatus;
  message: string;
}

const rawInput = ref('');
const isProcessing = ref(false);
const cancelRequested = ref(false);
const results = ref<ResultRow[]>([]);

const parsedInput = computed(() => {
  const tokens = rawInput.value.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);
  const valid: number[] = [];
  const skipped: string[] = [];
  const seen = new Set<number>();

  for (const token of tokens) {
    const num = Number(token);
    if (!Number.isInteger(num) || num <= 0) {
      skipped.push(token);
    } else if (!seen.has(num)) {
      seen.add(num);
      valid.push(num);
    }
  }

  return { valid, skipped };
});

const parsedIds = computed(() => parsedInput.value.valid);
const skippedTokens = computed(() => parsedInput.value.skipped);

const processedCount = computed(() => results.value.length);
const doneCount = computed(() => results.value.filter((r) => r.status === 'done').length);
const queuedCount = computed(() => results.value.filter((r) => r.status === 'queued').length);
const errorCount = computed(() => results.value.filter((r) => r.status === 'error').length);

const statusLabel = (status: RowStatus): string => {
  switch (status) {
    case 'done': return 'Done';
    case 'queued': return 'Queued';
    case 'error': return 'Failed';
    case 'skipped': return 'Skipped';
  }
};

const statusTagType = (status: RowStatus): 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case 'done': return 'success';
    case 'queued': return 'warning';
    case 'error': return 'danger';
    case 'skipped': return 'info';
  }
};

const reset = () => {
  rawInput.value = '';
  results.value = [];
  cancelRequested.value = false;
};

const transcribeOne = async (id: number): Promise<ResultRow> => {
  try {
    const response = await callLogService.transcribeCallLog({ id });
    if (response?.error) {
      return { id, status: 'error', message: response.error };
    }
    if (response?.lines && response.lines.length > 0) {
      return { id, status: 'done', message: 'Already transcribed — fetched from cache' };
    }
    return { id, status: 'queued', message: 'Queued for backend processing' };
  } catch (err) {
    return { id, status: 'error', message: util.getGraphqlError(err, false) };
  }
};

const startBatch = async () => {
  if (!parsedIds.value.length || isProcessing.value) {
    return;
  }

  isProcessing.value = true;
  cancelRequested.value = false;
  results.value = [];

  for (const id of parsedIds.value) {
    if (cancelRequested.value) {
      results.value.push({ id, status: 'skipped', message: 'Cancelled before processing' });
      continue;
    }
    const result = await transcribeOne(id);
    results.value.push(result);
  }

  isProcessing.value = false;
};
</script>

<style scoped lang="scss">
.page-description {
  color: #606266;
  margin-bottom: 16px;
  text-align: left;
  font-size: 14px;
}

.parse-summary {
  margin-top: 6px;
  font-size: 13px;
  color: #606266;

  .text-muted {
    color: #909399;
  }
}

.results-section {
  margin-top: 20px;

  hr {
    margin-bottom: 16px;
  }
}

.progress-line {
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.results-section :deep(.el-tag) {
  height: 24px;
  line-height: 22px;
  padding: 0 12px;
  min-width: 78px;
  text-align: center;
  box-sizing: border-box;
  border-radius: 12px;
  margin: 0;
}
</style>
